/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import LexicalClickableLinkPlugin from "@lexical/react/LexicalClickableLinkPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import useLexicalEditable from "@lexical/react/useLexicalEditable";
import { useRef, useState } from "react";

import { Template } from "@/features/templates/types";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { TemplateHeader } from "../Layout/TemplateHeader";
import { useSettings } from "./context/SettingsContext";
import { AutoSaveTemplate } from "./plugins/AutoSavePlugin/AutoSaveTemplate";
import CodeActionMenuPlugin from "./plugins/CodeActionMenuPlugin";
import CollapsiblePlugin from "./plugins/CollapsiblePlugin";
import ComponentPickerPlugin from "./plugins/ComponentPickerPlugin";
import DragDropPaste from "./plugins/DragDropPastePlugin";
import DraggableBlockPlugin from "./plugins/DraggableBlockPlugin";
import { EditorStorePlugin } from "./plugins/EditorStorePlugin";
import EmptyParagraphClickPlugin from "./plugins/EmptyParagraphClickPlugin";
import FloatingLinkEditorPlugin from "./plugins/FloatingLinkEditorPlugin";
import FloatingTextFormatToolbarPlugin from "./plugins/FloatingTextFormatToolbarPlugin";
import ImagesPlugin from "./plugins/ImagesPlugin";
import LinePlaceholderPlugin from "./plugins/LinePlaceholderPlugin";
import LinkPlugin from "./plugins/LinkPlugin";
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import MarkdownShortcutPlugin from "./plugins/MarkdownShortcutPlugin";
import MarkdownTransformerPlugin from "./plugins/MarkdownTransformerPlugin";
import OnChangePlugin from "./plugins/OnChangePlugin";
import PrepopulateTemplate from "./plugins/PrepopulatePlugin/PrepopulateTemplate";
import TabFocusPlugin from "./plugins/TabFocusPlugin";
import TableCellActionMenuPlugin from "./plugins/TableActionMenuPlugin";
import TableCellResizer from "./plugins/TableCellResizer";
import WordCountPlugin from "./plugins/WordCountPlugin";
import ContentEditable from "./ui/ContentEditable";
import { TitleInput } from "./ui/TitleInput";

const CONTENT_LABEL = "tab 1";

export const TemplateEditor = ({
  templateMetadata,
  isReadOnly,
}: {
  templateMetadata: Template;
  isReadOnly: boolean;
}) => {
  const {
    settings: { tableCellMerge, tableCellBackgroundColor },
  } = useSettings();
  const [editor] = useLexicalComposerContext();
  const isEditable = useLexicalEditable();
  const [floatingAnchorElem, setFloatingAnchorElem] =
    useState<HTMLDivElement | null>(null);
  const [blockElem, setBlockElem] = useState<HTMLDivElement | null>(null);
  const linePlaceholderRef = useRef<HTMLDivElement>(null);
  const [showAiWriter, setShowAiWriter] = useState<boolean>(false);

  const [showLinePlaceholder, setShowLinePlaceholder] = useState<boolean>(true);
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);

  const titleInputRef = useRef<HTMLTextAreaElement>(null);
  const floatingRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  const blockRef = (_blockElem: HTMLDivElement) => {
    if (_blockElem !== null) {
      setBlockElem(_blockElem);
    }
  };

  return (
    <div className=" flex flex-col h-full w-full">
      <TemplateHeader readOnly={isReadOnly} />
      <div className="flex flex-col h-full w-full">
        <DragDropPaste />
        <ComponentPickerPlugin
          setShowAiMenu={setShowAiWriter}
          showWriteWithAi={false}
        />

        <LinePlaceholderPlugin
          paragraphRef={linePlaceholderRef}
          placeholder={`Press '/' for commands`}
        />

        <EditorStorePlugin />
        <OnChangePlugin setShowPlaceholder={() => {}} />
        <PrepopulateTemplate template={templateMetadata} />
        <AutoSaveTemplate
          template={templateMetadata}
          isLinkEditMode={isLinkEditMode}
        />
        <HistoryPlugin />
        <MarkdownTransformerPlugin />
        <EmptyParagraphClickPlugin />
        <RichTextPlugin
          contentEditable={
            <div className="flex flex-row overflow-y-auto overflow-x-hidden h-[calc(100vh-68px)] print:overflow-visible print:h-auto w-full">
              <div
                ref={floatingRef}
                className="relative mx-auto flex w-full max-w-[750px]"
                id="lexical-editor"
              >
                <div
                  ref={blockRef}
                  className="relative w-full"
                  id="lexical-editor-container"
                  style={{ maxWidth: "100%" }}
                >
                  <div className="mt-14"></div>
                  <TitleInput
                    ref={titleInputRef}
                    content={templateMetadata}
                    setShowPlaceholders={() => {}}
                    showPlaceholders={false}
                    setShowLinePlaceholder={() => {}}
                    readOnly={isReadOnly}
                    type="template"
                  />
                  <div className="pb-80">
                    <ContentEditable />
                  </div>
                </div>
              </div>

              {/*process.env.NODE_ENV === "development" && <TreeViewPlugin />*/}
              <div className="sticky top-0 flex flex-col w-[80px]">
                <WordCountPlugin />
              </div>
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <MarkdownShortcutPlugin />
        <ListPlugin />
        <CheckListPlugin />
        <ListMaxIndentLevelPlugin maxDepth={7} />
        <TablePlugin
          hasCellMerge={tableCellMerge}
          hasCellBackgroundColor={tableCellBackgroundColor}
        />
        <TableCellResizer />
        <ImagesPlugin />
        <LinkPlugin />
        {!isEditable && <LexicalClickableLinkPlugin />}
        <HorizontalRulePlugin />
        <TabFocusPlugin />
        <TabIndentationPlugin />
        <CollapsiblePlugin />
        {floatingAnchorElem && (
          <>
            <DraggableBlockPlugin anchorElem={blockElem} />
            <CodeActionMenuPlugin anchorElem={floatingAnchorElem} />
            <FloatingLinkEditorPlugin
              anchorElem={floatingAnchorElem}
              isLinkEditMode={isLinkEditMode}
              setIsLinkEditMode={setIsLinkEditMode}
            />
            <TableCellActionMenuPlugin
              anchorElem={floatingAnchorElem}
              cellMerge={true}
            />
            <FloatingTextFormatToolbarPlugin
              anchorElem={floatingAnchorElem}
              setIsLinkEditMode={setIsLinkEditMode}
              isTemplate={true}
            />
          </>
        )}
      </div>
    </div>
  );
};
